import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "wouter";
import {
  blockOptionalCollection,
  isOptionalCollectionBlocked,
  privacySubjectId,
  readPrivacyChoices,
  writePrivacyChoices,
  OPEN_PRIVACY_CHOICES_EVENT,
  subscribePrivacyChoices,
  globalPrivacyControlEnabled,
  type PrivacyChoices,
} from "@/lib/privacy-choices";
import "./privacy-consent-banner.css";

/**
 * PLACEHOLDER COPY.
 * The sentences in this banner are not the privacy policy. The owner still
 * has to approve the final wording. The controls themselves (necessary,
 * analytics, marketing, save, withdraw) are the working consent UI.
 */

type Draft = { analytics: boolean; marketing: boolean };
const choiceButtonClass = "privacy-choice-button";

const PENDING_CHOICE_MESSAGE =
  "Your privacy choice has not been confirmed by the server. Optional tracking stays off in this browser; retry to confirm it.";

async function postChoice(
  choice: Draft & { action: PrivacyChoices["action"]; source: "banner" | "preferences" | "gpc" },
): Promise<PrivacyChoices> {
  const base = String(import.meta.env.BASE_URL ?? "/").replace(/\/$/, "");
  const response = await fetch(`${base}/api/privacy/consent`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ subjectId: privacySubjectId(), ...choice }),
  });
  if (!response.ok) throw new Error("consent_not_recorded");
  const saved = await response.json().catch(() => null) as {
    necessary?: unknown;
    analytics?: unknown;
    marketing?: unknown;
    action?: unknown;
  } | null;
  if (
    !saved ||
    saved.necessary !== true ||
    saved.analytics !== choice.analytics ||
    saved.marketing !== choice.marketing ||
    saved.action !== choice.action
  ) {
    throw new Error("consent_not_confirmed");
  }
  return writePrivacyChoices(choice);
}

export function PrivacyConsentBanner() {
  const [stored, setStored] = useState<PrivacyChoices | null>(() => readPrivacyChoices());
  const [open, setOpen] = useState(
    () => readPrivacyChoices() == null,
  );
  const [custom, setCustom] = useState(false);
  const banner = useRef<HTMLDivElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const pill = useRef<HTMLButtonElement>(null);
  const restoreFocus = useRef(false);
  const [draft, setDraft] = useState<Draft>(() =>
    isOptionalCollectionBlocked()
      ? { analytics: false, marketing: false }
      : {
          analytics: readPrivacyChoices()?.analytics ?? false,
          marketing: readPrivacyChoices()?.marketing ?? false,
        },
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(() =>
    isOptionalCollectionBlocked() ? PENDING_CHOICE_MESSAGE : null,
  );
  const [saveStatus, setSaveStatus] = useState<string | null>(null);
  const gpcRecorded = useRef(false);
  const gpc = globalPrivacyControlEnabled();
  useEffect(() => {
    if (!gpc || gpcRecorded.current) return;
    setOpen(true);
    setCustom(false);
    const choice = readPrivacyChoices();
    if (choice?.action === "withdraw") return;
    gpcRecorded.current = true;
    blockOptionalCollection();
    void postChoice({ analytics: false, marketing: false, action: "withdraw", source: "gpc" })
      .then(setStored)
      .catch(() => { setError(PENDING_CHOICE_MESSAGE); gpcRecorded.current = false; });
  }, [gpc]);

  function openPreferences() {
    const latest = readPrivacyChoices();
    setStored(latest);
    setDraft(isOptionalCollectionBlocked()
      ? { analytics: false, marketing: false }
      : { analytics: latest?.analytics ?? false, marketing: latest?.marketing ?? false });
    setSaveStatus(null);
    restoreFocus.current = true;
    setCustom(true);
    setOpen(true);
  }

  useEffect(() => {
    window.addEventListener(OPEN_PRIVACY_CHOICES_EVENT, openPreferences);
    const unsubscribe = subscribePrivacyChoices(() => setStored(readPrivacyChoices()));
    return () => {
      window.removeEventListener(OPEN_PRIVACY_CHOICES_EVENT, openPreferences);
      unsubscribe();
    };
  }, []);

  useLayoutEffect(() => {
    if (!open || !banner.current) return;
    const root = document.documentElement;
    const previousPadding = document.body.style.paddingBottom;
    const previousScrollPadding = root.style.scrollPaddingBottom;
    const originalPadding = getComputedStyle(document.body).paddingBottom || "0px";
    const update = () => {
      const height = banner.current?.getBoundingClientRect().height ?? 0;
      document.body.style.paddingBottom = `calc(${originalPadding} + ${height}px)`;
      root.style.scrollPaddingBottom = `${height + 8}px`;
    };
    update();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(banner.current);
    window.addEventListener("resize", update);
    if (restoreFocus.current) heading.current?.focus({ preventScroll: true });
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", update);
      document.body.style.paddingBottom = previousPadding;
      root.style.scrollPaddingBottom = previousScrollPadding;
    };
  }, [open]);

  useEffect(() => {
    if (!open && restoreFocus.current) {
      pill.current?.focus({ preventScroll: true });
      restoreFocus.current = false;
    }
  }, [open]);

  async function save(choice: Draft, action: PrivacyChoices["action"]) {
    if (globalPrivacyControlEnabled()) {
      choice = { analytics: false, marketing: false };
      action = "withdraw";
    }
    setSaving(true);
    setError(null);
    setSaveStatus(null);
    // A slow or failed request must not keep the page covered. Only a verified
    // acknowledgement persists a choice; the pill remains available for retry.
    setOpen(false);
    const reducesOptionalCollection =
      !choice.analytics || !choice.marketing || action === "withdraw" ||
      (stored?.analytics === true && !choice.analytics) ||
      (stored?.marketing === true && !choice.marketing);
    if (reducesOptionalCollection) blockOptionalCollection();
    try {
      const next = await postChoice({ ...choice, action, source: globalPrivacyControlEnabled() ? "gpc" : open && stored == null ? "banner" : "preferences" });
      setStored(next);
      setOpen(false);
      setSaveStatus(
        action === "withdraw"
          ? "Privacy choice saved. Optional analytics and marketing are off."
          : "Privacy choices saved.",
      );
    } catch {
      setError(PENDING_CHOICE_MESSAGE);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <button
        ref={pill}
        type="button"
        className="privacy-choices-pill"
        hidden={open}
        aria-expanded={open}
        aria-controls="privacy-consent-banner"
        data-testid="button-privacy-choices"
        onClick={openPreferences}
      >
        Privacy choices
      </button>
      {!open && error && <p role="alert" className="privacy-choice-error">{error}</p>}
      {saveStatus && (
        <p
          role="status"
          data-testid="privacy-consent-saved"
          className="fixed right-4 top-4 z-40 max-w-[min(24rem,calc(100vw-2rem))] rounded-lg border border-emerald-400/30 bg-[#0b0d12] px-3 py-2 text-sm text-emerald-200 shadow-lg"
        >
          {saveStatus}
        </p>
      )}
      {open && (
        <div
          ref={banner}
          id="privacy-consent-banner"
          className="privacy-consent-banner"
          role="region"
          aria-labelledby="privacy-consent-title"
          data-testid="privacy-consent-banner"
        >
          <div className="privacy-consent-content">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-300" data-testid="privacy-consent-placeholder">
              Placeholder copy — not the privacy policy
            </p>
            <div>
              <h2 ref={heading} tabIndex={-1} id="privacy-consent-title" className="text-sm font-semibold">Privacy choices (placeholder copy)</h2>
              {custom && <p className="mt-1 text-sm text-white/70">
                Placeholder: necessary storage keeps the app working. Analytics and marketing stay off until you opt in. You can withdraw either one later. The owner still needs to approve this wording.
              </p>}
            </div>
            {gpc && <p className="text-xs text-white/70">Your browser's Global Privacy Control signal is on, so optional analytics and marketing are off.</p>}
            {custom && <fieldset className="space-y-3">
              <label className="flex items-start gap-3 text-sm">
                <input type="checkbox" checked disabled data-testid="checkbox-consent-necessary" />
                <span>
                  <span className="font-medium">Necessary</span>
                  <span className="mt-0.5 block text-white/60">Placeholder: required for security and remembering this choice. Always on.</span>
                </span>
              </label>
              <label className="flex items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={draft.analytics}
                  disabled={gpc}
                  onChange={(event) => setDraft((current) => ({ ...current, analytics: event.target.checked }))}
                  data-testid="checkbox-consent-analytics"
                />
                <span>
                  <span className="font-medium">Analytics</span>
                  <span className="mt-0.5 block text-white/60">Placeholder: counts of pages, signups and features only. No health, DNA, lab or payment details.</span>
                </span>
              </label>
              <label className="flex items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={draft.marketing}
                  disabled={gpc}
                  onChange={(event) => setDraft((current) => ({ ...current, marketing: event.target.checked }))}
                  data-testid="checkbox-consent-marketing"
                />
                <span>
                  <span className="font-medium">Marketing</span>
                  <span className="mt-0.5 block text-white/60">Placeholder: optional product messages. No health data is used.</span>
                </span>
              </label>
            </fieldset>}
            {error && <p role="alert" className="text-sm text-rose-300">{error}</p>}
            <div className="privacy-choice-actions">
              <button
                type="button"
                disabled={saving}
                className={choiceButtonClass}
                data-testid="button-consent-reject"
                onClick={() => {
                  const refusal = { analytics: false, marketing: false };
                  setDraft(refusal);
                  void save(refusal, "withdraw");
                }}
              >
                Reject optional
              </button>
              <button
                type="button"
                disabled={saving}
                className={choiceButtonClass}
                data-testid="button-consent-accept"
                onClick={() => void save({ analytics: true, marketing: true }, stored ? "update" : "grant")}
              >
                Accept all
              </button>
              <button
                type="button"
                disabled={saving}
                className={choiceButtonClass}
                data-testid="button-consent-save"
                onClick={() => custom ? void save(draft, !draft.analytics && !draft.marketing ? "withdraw" : stored ? "update" : "grant") : setCustom(true)}
              >
                {custom ? "Save choices" : "Customize"}
              </button>
            </div>
            {custom && <Link href="/legal/privacy" className="self-center text-sm text-primary underline">Privacy policy</Link>}
          </div>
        </div>
      )}
    </>
  );
}
